import { CanvasBounds, CanvasRenderer, RenderedCanvas } from "@rosette/web_components/src/generic/render/RenderedCanvas.js";
import { ParticleSystem } from "@rosette/web_components/src/game/fireworks/ParticleSystem.js";
import { rgba } from "@/components/game/render/render.js";
import { clamp, min } from "@rosette/ts/src/util/numbers.js";
import type { Seconds } from "@rosette/ts/src/util/units.js";
import { Vec2 } from "@rosette/ts/src/util/Vec2.js";
import { getTimeSeconds, LONG_TIME_AGO } from "@rosette/ts/src/util/utils.js";
import React, { useMemo, useRef } from "react";
import { FireworksResources } from "@/components/game/fireworks/FireworksResources.js";
import type { AssetLoader } from "@/components/assets/AssetLoader.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import type { GameTheme } from "@/components/game/theme/GameTheme.js";
import { rand } from "@rosette/ts/src/util/random.js";


class Firework {
    static readonly EXPLODE_PARTICLE_SPEED = 360 / 2560;
    static readonly TRAIL_PARTICLE_SPEED = 90 / 2560;
    static readonly GRAVITY = 600 / 2560;

    private readonly createTime: Seconds;
    private readonly lifetime: Seconds;
    private readonly origin: Vec2;
    private readonly dest: Vec2;
    private readonly journey: Vec2;
    private readonly r: number;
    private readonly g: number;
    private readonly b: number;
    private readonly rocketSoundID: number | null;
    private readonly soundVolume: number;

    constructor(
        origin: Vec2,
        dest: Vec2,
        speed: number,
        r: number,
        g: number,
        b: number,
        rocketSoundID: number | null,
        soundVolume: number,
    ) {
        this.createTime = getTimeSeconds();
        this.lifetime = dest.dist(origin) / speed;
        this.origin = origin;
        this.dest = dest;
        this.journey = dest.sub(origin);
        this.r = r;
        this.g = g;
        this.b = b;
        this.rocketSoundID = rocketSoundID;
        this.soundVolume = soundVolume;
    }

    getRocketSoundID(): number | null {
        return this.rocketSoundID;
    }

    getSoundVolume(): number {
        return this.soundVolume;
    }

    simulate(particles: ParticleSystem, time: Seconds, dt: Seconds, width: number, height: number): boolean {
        dt = Math.min(0.2, dt);
        const ageSecs = time - this.createTime;
        const age = min(1.0, ageSecs / this.lifetime);
        const loc = this.origin.add(this.journey.mul(age));
        const moved = this.journey.mul(dt / this.lifetime);
        const movedLength = moved.length();

        // Spawn particle explosion!
        if (age >= 1) {
            particles.spawnExplosion(
                360, loc.x, loc.y,
                Firework.EXPLODE_PARTICLE_SPEED * Math.max(width, height),
                1,
                this.r, this.g, this.b,
                true,
                Firework.GRAVITY * Math.max(width, height),
            );
            return true;
        }

        // Spawn particle trail.
        let trailParticlesToSpawn = Math.floor(movedLength);
        if (Math.random() < movedLength % 1.0) {
            trailParticlesToSpawn += 1;
        }
        for (let index = 0; index < trailParticlesToSpawn; ++index) {
            const spawnLoc = loc.sub(moved.mul(index / movedLength));
            particles.spawnExplosion(
                2, spawnLoc.x, spawnLoc.y,
                Firework.TRAIL_PARTICLE_SPEED * Math.max(width, height),
                0.5,
                255, 255, 255,
                false, 0,
            );
        }
        return false;
    }
}

class FireworksRenderer extends CanvasRenderer {

    static readonly FIREWORK_ROCKET_SOUND_COOLDOWN: Seconds = 0.1;
    static readonly FIREWORK_MAX_SOUNDS = 4;

    static readonly START_FIREWORKS_DELAY: Seconds = 1.0;
    static readonly MIN_GENERATE_INTERVAL: Seconds = 3.0;
    static readonly MAX_GENERATE_INTERVAL: Seconds = 6.0;
    static readonly LANDSCAPE_FIREWORK_REGIONS = [
        { min_x: 0.1, max_x: 0.25, min_y: 0.15, max_y: 0.5 },
        { min_x: 0.75, max_x: 0.9, min_y: 0.15, max_y: 0.5 },
    ];

    static readonly PORTRAIT_FIREWORK_REGIONS = [
        { min_y: 0.05, max_y: 0.2, min_x: 0.15, max_x: 0.5 },
        { min_y: 0.05, max_y: 0.2, min_x: 0.5, max_x: 0.85 },
    ];

    static readonly FIREWORK_SPEED = 600 / 1280;

    static readonly PARTICLE_RADIUS = 6 / 2560;
    static readonly MIN_PARTICLE_RADIUS = 2.4;
    static readonly MAX_PARTICLE_RADIUS = 4.5;

    private readonly isMobile: boolean;
    private readonly theme: GameTheme;
    private readonly resources: FireworksResources;
    private readonly particles: ParticleSystem;
    private readonly fireworks: Firework[];
    private readonly nextFireworkTimes: Seconds[];
    private lastFireworkRocketSoundTime = LONG_TIME_AGO;
    private fireworkCount = 0;

    constructor(
        assetLoader: AssetLoader,
        isMobile: boolean,
        theme: GameTheme,
    ) {
        super();
        this.isMobile = isMobile;
        this.theme = theme;
        this.resources = new FireworksResources(assetLoader);
        this.particles = new ParticleSystem();
        this.fireworks = [];
        this.nextFireworkTimes = [];
        for (const _ of this.getRegions()) {
            this.nextFireworkTimes.push(getTimeSeconds() + FireworksRenderer.START_FIREWORKS_DELAY);
        }
    }

    override attachListeners(): () => void {
        return () => {
            for (const firework of this.fireworks) {
                const soundID = firework.getRocketSoundID();
                if (soundID != null) {
                    this.resources.getRocketSound().stop(soundID);
                }
            }
        };
    }

    getRegions(): { min_x: number; max_x: number; min_y: number; max_y: number }[] {
        if (this.isMobile) {
            return FireworksRenderer.PORTRAIT_FIREWORK_REGIONS;
        } else {
            return FireworksRenderer.LANDSCAPE_FIREWORK_REGIONS;
        }
    }

    simulateFireworks(time: Seconds, dt: Seconds, width: number, height: number) {
        // Check if we should generate any new fireworks.
        const regions = this.getRegions();
        for (let index = 0; index < this.nextFireworkTimes.length; ++index) {
            const timeToFirework = this.nextFireworkTimes[index] - time;
            if (timeToFirework > 0)
                continue;

            const timeToNextFirework = rand(
                FireworksRenderer.MIN_GENERATE_INTERVAL,
                FireworksRenderer.MAX_GENERATE_INTERVAL,
            );
            this.nextFireworkTimes[index] = time + timeToNextFirework;

            const region = regions[index];
            const x2 = width * rand(region.min_x, region.max_x);
            const y2 = height * rand(region.min_y, region.max_y);
            let x1, y1;
            if (this.isMobile) {
                x1 = (x2 < width / 2 ? 0 : width);
                y1 = (1.75 + 0.5 * rand()) * height / 3;
            } else {
                x1 = width * rand(region.min_x, region.max_x);
                y1 = height;
            }

            const colour = this.theme.getFireworkColour();

            const timeSinceRocketSound = time - this.lastFireworkRocketSoundTime;
            let rocketSoundID: number | null = null;
            let fireworkVolume = 0.0;

            this.fireworkCount += 1;
            if (this.fireworkCount < FireworksRenderer.FIREWORK_MAX_SOUNDS) {
                fireworkVolume = 1.0;
            } else {
                const pastMax = this.fireworkCount - FireworksRenderer.FIREWORK_MAX_SOUNDS;
                fireworkVolume = clamp(1.0 - 0.1 * pastMax, 0.25, 1.0);
            }

            if (timeSinceRocketSound >= FireworksRenderer.FIREWORK_ROCKET_SOUND_COOLDOWN) {
                rocketSoundID = this.resources.getRocketSound().play(true, fireworkVolume);
                this.lastFireworkRocketSoundTime = time;
            }

            this.fireworks.push(new Firework(
                new Vec2(x1, y1),
                new Vec2(x2, y2),
                FireworksRenderer.FIREWORK_SPEED * height,
                colour.r, colour.g, colour.b,
                rocketSoundID,
                fireworkVolume,
            ));
        }

        // Simulate existing fireworks.
        let index = this.fireworks.length - 1;
        while (index >= 0) {
            try {
                const firework = this.fireworks[index];
                if (firework.simulate(this.particles, time, dt, width, height)) {
                    const rocketSoundID = firework.getRocketSoundID();
                    if (rocketSoundID != null) {
                        this.resources.getRocketSound().stop(rocketSoundID);
                    }
                    this.resources.getExplodeSound().play(false, firework.getSoundVolume());
                    this.fireworks.splice(index, 1);
                }
            } finally {
                index -= 1;
            }
        }
    }

    override calculateRenderBounds(bounds: CanvasBounds): CanvasBounds {
        return new CanvasBounds(
            bounds.rect,
            Math.ceil(bounds.width / 3),
            Math.ceil(bounds.height / 3),
        );
    }

    override render(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        dt: Seconds,
    ): void {
        this.clearCanvas(ctx, width, height);

        const time = getTimeSeconds();
        this.simulateFireworks(time, dt, width, height);

        const radius = clamp(
            FireworksRenderer.PARTICLE_RADIUS * Math.max(width, height),
            FireworksRenderer.MIN_PARTICLE_RADIUS,
            FireworksRenderer.MAX_PARTICLE_RADIUS,
        );

        this.particles.forEach((age, x, y, r, g, b) => {
            const a = (0.25 * Math.cos(12 * Math.PI * age) + 0.75) * Math.sqrt(1 - age);
            ctx.fillStyle = rgba(r, g, b, a);
            ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
        });
    }
}

function useFireworksRenderer(isMobile: boolean): FireworksRenderer {
    const assetLoader = useAssetLoader();
    const theme = useGameTheme();
    return useMemo(() => {
        return new FireworksRenderer(assetLoader, isMobile, theme);
    }, [assetLoader, isMobile, theme]);
}


interface FireworksProps {
    isMobile: boolean;
}


export function Fireworks({ isMobile }: FireworksProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const renderer = useFireworksRenderer(isMobile);
    return (
        <RenderedCanvas
            canvasRef={canvasRef}
            className="absolute left-0 top-0 w-full h-full z-99 pointer-events-none"
            renderer={renderer}
            updateEveryFrame={true} />
    );
}
